import { FC, Key, useEffect, useMemo, useState } from 'react';
import { Button, Form, Input, Modal, PopConfirm, Select, Table, message, MultiSelect } from '@maxtropy/components';
import { debounce, isNil, isEmpty } from 'lodash-es';
import {
  apiV2CircuitListPost,
  apiV2DistributionCabinetGetUnBindListPost,
  apiV2DistributionCabinetListPost,
  apiV2DistributionCabinetRoomCabinetTreePost,
  apiV2DistributionCabinetUpsertDistributionRoomPost,
  apiV2OuListOwnedPost,
  V2OuListOwnedPostResponse,
} from '@maxtropy/device-customer-apis-v2';
import { PlusOutlined } from '@ant-design/icons';
import styles from './index.module.scss';
import { ColumnsType } from 'antd/es/table';
import { v4 as uuidv4 } from 'uuid';
import { CabinetItem, CircuitItem, levelOptions, RoomItem } from '../../utils';

interface AddRoomModalProps {
  onClose: () => void;
  updateFn: () => void;
  visible: boolean;
  room?: any;
}

type CabinetInfo = {
  cabinetName: string;
  id: string | number;
};

const isUuid = (str: string) => {
  const uuidRegex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
  return uuidRegex.test(str);
};
export type ExtendedCabinetItem = CabinetItem & {
  disabled?: boolean;
};
export type OuInfoProps = Exclude<V2OuListOwnedPostResponse['list'], undefined>[number];
const AddRoomModal: FC<AddRoomModalProps> = ({ room, onClose, updateFn, visible }) => {
  const [form] = Form.useForm();
  const [treeData, setTreeData] = useState<RoomItem[]>([]);
  const [circuitList, setCircuitList] = useState<CircuitItem[]>([]);
  const [cabinetList, setCabinetList] = useState<CabinetItem[]>([]);
  const [ouItems, setOuItems] = useState<OuInfoProps[]>([]);

  const level = Form.useWatch('level', form); // 填报类型
  const list = Form.useWatch('list', form); // 配电柜列表
  const ouIdList = Form.useWatch('ouIdList', form); // ou列表

  const [searchValue, setSearchValue] = useState<string>();

  useEffect(() => {
    if (room && treeData.length > 0) {
      // 递归查找目标节点的父节点
      const findParentNode = (nodes: RoomItem[], targetId: number, parent: RoomItem | null = null): RoomItem | null => {
        for (const node of nodes) {
          // 如果找到目标节点，返回其父节点
          if (node.id === targetId) {
            return parent;
          }

          // 如果当前节点有子节点，递归搜索子节点
          if (node.children && node.children.length > 0) {
            const foundParent = findParentNode(node.children, targetId, node);
            if (foundParent) {
              return foundParent;
            }
          }
        }
        return null;
      };

      // 查找父节点
      const parentNode = findParentNode(treeData, room.id, null);
      // 设置表单值
      form.setFieldsValue({
        level: room.level - 1,
        name: room.name,
        ouIdList: room.ouIdList,
        circuitId: room?.circuitIdList,
        circuitMonitorId: room?.circuitMonitorId,
        parentId: parentNode ? parentNode.id : undefined, // 如果找到父节点则设置其ID
        list: (room.children ?? [])
          .filter((m: any) => m.type === 1)
          .map((i: any) => ({ cabinetName: i.name, id: i.id, disabled: true })),
      });
    }
  }, [room, treeData]);

  useEffect(() => {
    apiV2CircuitListPost({}).then(res => {
      setCircuitList(res.list ?? []);
    });
    apiV2DistributionCabinetRoomCabinetTreePost({}).then(res => {
      setTreeData(res.list ?? []);
    });

    // 查询 ou
    apiV2OuListOwnedPost({}).then(res => {
      setOuItems(res?.list ?? []);
    });
  }, []);

  useEffect(() => {
    if (isEmpty(ouIdList)) return;
    apiV2DistributionCabinetGetUnBindListPost({ ids: ouIdList }).then(res => {
      setCabinetList(res.list ?? []);
    });
  }, [ouIdList]);

  const circuitOptions = useMemo(() => {
    return (circuitList ?? []).map(i => ({ label: i.name, value: i.id }));
  }, [circuitList]);

  const cabinetOptins = useMemo(() => {
    return (cabinetList ?? [])
      .map(i => ({ label: i.name, value: i.id }))
      .filter(i => !(list ?? []).some((m: CabinetInfo) => m.id === i.value));
  }, [cabinetList, list]);

  const parentOptions = useMemo(() => {
    if (isNil(level) || level <= 0) {
      return [];
    }

    // 获取当前 room 的所有子孙节点（只收集 type === 0）
    const collectAllSubRoomIds = (nodes: RoomItem[], targetId: number): number[] => {
      const result: number[] = [];

      const traverse = (items: RoomItem[]) => {
        for (const item of items) {
          if (item.id === targetId) {
            collect(item);
            break;
          } else if (item.children?.length) {
            traverse(item.children);
          }
        }
      };

      const collect = (node: RoomItem) => {
        if (node.type === 0) {
          result.push(node.id!);
          if (node.children?.length) {
            node.children.forEach(child => collect(child));
          }
        }
      };

      traverse(nodes);
      return result;
    };

    const excludedIds = room?.id ? collectAllSubRoomIds(treeData, room.id) : [];

    // 递归查找符合当前层级且未被排除的节点
    const findNodesByLevel = (nodes: RoomItem[], currentLevel: number, result: any[] = []) => {
      for (const node of nodes) {
        if (node.level === currentLevel && node.type === 0 && !excludedIds.includes(node.id!)) {
          result.push({
            label: node.name,
            value: node.id,
          });
        }
        if (node.children?.length) {
          findNodesByLevel(node.children, currentLevel, result);
        }
      }
      return result;
    };

    return findNodesByLevel(treeData, level);
  }, [level, treeData, room?.id]);

  // 弹框确认
  const onOk = () => {
    form.validateFields().then(values => {
      const cabinetIdList = (values.list ?? [])
        .filter((k: CabinetInfo) => !isUuid(k.id as string))
        .map((m: CabinetInfo) => m.id);
      const cabinetNameList = (values.list ?? [])
        .filter((k: CabinetInfo) => isUuid(k.id as string))
        .map((m: CabinetInfo) => m.cabinetName);
      apiV2DistributionCabinetUpsertDistributionRoomPost({
        id: room?.id,
        name: values.name,
        parentId: values?.parentId,
        circuitId: values?.circuitId,
        ouIdList: values.ouIdList,
        cabinetIdList,
        cabinetNameList,
        circuitMonitorId: values?.circuitMonitorId,
      }).then(() => {
        updateFn();
        onClose();
      });
    });
  };

  const onSearch = debounce((value: string) => {
    setSearchValue(value);
  }, 10);

  const onBlur = (index: number) => {
    if (searchValue && searchValue.trim()) {
      form.setFieldValue(['list', index, 'cabinetName'], searchValue.trim());
    }
  };

  const onChange = (value: number, index: number) => {
    form.setFieldValue(['list', index, 'id'], value);
    form.setFieldValue(['list', index, 'cabinetName'], cabinetList.find(i => i.id === value)?.name || '');
  };

  const addRow = () => {
    const newRow = { id: uuidv4() };
    const prevList = form.getFieldValue('list') || [];
    prevList.push(newRow);
    form.setFieldsValue({ list: prevList });
  };

  const columns: ColumnsType<ExtendedCabinetItem> = [
    {
      title: '配电柜名称',
      dataIndex: 'cabinetName',
      width: '15%',
      render(value, record, index) {
        return (
          <Form.Item
            name={['list', index, 'cabinetName']}
            rules={[{ required: true, message: '请输入配电柜名称' }]}
            style={{ marginBottom: 0 }}
          >
            <Select
              placeholder="请输入/选择"
              allowClear
              showSearch
              disabled={record?.disabled ?? false}
              optionFilterProp="label"
              onSearch={onSearch}
              onChange={(value: number) => onChange(value, index)}
              onBlur={() => onBlur(index)}
              options={cabinetOptins}
            />
          </Form.Item>
        );
      },
    },

    {
      title: '操作',
      ellipsis: true,
      width: '10%',
      render(value, record, index) {
        return (
          <PopConfirm
            placement="top"
            title={'是否确认删除?'}
            onConfirm={() => {
              const prevList = form.getFieldValue('list');
              const removeId = prevList.findIndex((item: CabinetInfo) => {
                return item.id === record.id;
              });
              prevList.splice(removeId, 1);
              form.setFieldsValue({ list: prevList });
            }}
          >
            <Button type="link">删除</Button>
          </PopConfirm>
        );
      },
    },
  ];

  return (
    <Modal
      title={`${room ? '编辑站房' : '新建站房'}`}
      open={visible}
      onOk={onOk}
      contentClassName="modal-form-content"
      onCancel={onClose}
    >
      <Form form={form}>
        <Form.Item
          label="站房名称"
          rules={[
            { required: true, message: '请输入站房名称' },
            { max: 50, message: '最多输入50字' },
          ]}
          name="name"
        >
          <Input placeholder="请选择" />
        </Form.Item>
        <Form.Item label="站房层级" rules={[{ required: true }]} name="level">
          <Select
            style={{ width: '100%' }}
            placeholder="请选择"
            options={levelOptions}
            onChange={() => {
              form.setFieldValue('parentId', undefined);
            }}
          />
        </Form.Item>
        <Form.Item noStyle dependencies={['level']}>
          {({ getFieldValue }) => {
            return (
              getFieldValue('level') > 0 && (
                <Form.Item label="上级站房" rules={[{ required: true }]} name="parentId">
                  <Select style={{ width: '100%' }} placeholder="请选择" options={parentOptions} />
                </Form.Item>
              )
            );
          }}
        </Form.Item>
        <Form.Item label="站房进线回路" name="circuitId">
          <Select
            style={{ width: '100%' }}
            placeholder="请选择"
            mode="multiple"
            showSearch
            optionFilterProp="label"
            options={circuitOptions}
          />
        </Form.Item>
        <Form.Item label="站房组态监测回路" name="circuitMonitorId">
          <Select
            style={{ width: '100%' }}
            placeholder="请选择"
            mode="multiple"
            showSearch
            optionFilterProp="label"
            options={circuitOptions}
          />
        </Form.Item>
        <Form.Item label="所属运营单元" name="ouIdList" rules={[{ required: true }]}>
          <MultiSelect
            placeholder="请选择"
            style={{ width: '100%' }}
            showSearch
            options={ouItems.map(o => ({ label: o.name, value: o.id }))}
            onChange={e => {
              const list = (room.children ?? [])
                .filter((m: any) => m.type === 1)
                .map((i: any) => ({ cabinetName: i.name, id: i.id, disabled: true }));
              form.setFieldValue('list', list);
            }}
          ></MultiSelect>
        </Form.Item>
        <div style={{ color: 'var(--mx-form-item-label-color)' }}>配电柜信息</div>
        <Form.Item name="list" valuePropName="dataSource" noStyle>
          <Table style={{ marginTop: 8 }} rowKey="id" columns={columns} />
        </Form.Item>

        <Button
          onClick={() => {
            if (!ouIdList) {
              message.error({
                content: '请先选择所属运营单元！',
              });
              return;
            }
            addRow();
          }}
          type="dashed"
          wrapClassName={styles.addRow}
          className={styles.rowInnerBtn}
          ghost
          icon={<PlusOutlined />}
        >
          新增/选择配电柜
        </Button>
      </Form>
    </Modal>
  );
};

export default AddRoomModal;
